import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Toaster } from "sonner";
import UpgradeModal from "@/components/UpgradeModal";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import NewExpedition from "@/pages/NewExpedition";
import ExpeditionView from "@/pages/ExpeditionView";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AdminSetup from "@/pages/AdminSetup";
import Admin from "@/pages/Admin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Unsubscribe from "@/pages/Unsubscribe";
import Account from "@/pages/Account";
import Climbers from "@/pages/Climbers";
import Badges from "@/pages/Badges";
import Billing from "@/pages/Billing";
import OnboardingQuiz from "@/pages/OnboardingQuiz";
import QuizHistory from "@/pages/QuizHistory";
import TeacherDashboard from "@/pages/TeacherDashboard";
import TeacherSetupGuide from "@/pages/TeacherSetupGuide";
import ClassRoster from "@/pages/ClassRoster";
import StudentLogin from "@/pages/StudentLogin";
import StudentLoginGuide from "@/pages/StudentLoginGuide";
import StudentOnboarding from "@/pages/StudentOnboarding";
import StudentDashboard from "@/pages/StudentDashboard";
import StudentExpeditionView from "@/pages/StudentExpeditionView";
import TeacherTerms from "@/pages/TeacherTerms";
import TeacherLiveMonitor from "@/pages/TeacherLiveMonitor";
import PurchaseOrderRequest from "@/pages/PurchaseOrderRequest";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Footer from "@/components/Footer";
import TermsUpdateModal from "@/components/TermsUpdateModal";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-mm-navy/60">Loading trail...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return (
    <GoogleOAuthProvider clientId={googleClientId || ""}>
      <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <UpgradeModal />
        <TermsUpdateModal />
        <div className="min-h-screen flex flex-col">
          <div className="flex-1 flex flex-col">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
              <Route path="/account" element={<Protected><Account /></Protected>} />
              <Route path="/climbers" element={<Protected><Climbers /></Protected>} />
              <Route path="/climbers/:id/onboarding" element={<Protected><OnboardingQuiz /></Protected>} />
              <Route path="/climbers/:id/quiz-history" element={<Protected><QuizHistory /></Protected>} />
              <Route path="/badges" element={<Protected><Badges /></Protected>} />
              <Route path="/billing" element={<Protected><Billing /></Protected>} />
              <Route path="/expeditions/new" element={<Protected><NewExpedition /></Protected>} />
              <Route path="/expeditions/:id" element={<Protected><ExpeditionView /></Protected>} />
              <Route path="/teacher/dashboard" element={<Protected><TeacherDashboard /></Protected>} />
              <Route path="/teacher/setup-guide" element={<Protected><TeacherSetupGuide /></Protected>} />
              <Route path="/teacher/classes/:id" element={<Protected><ClassRoster /></Protected>} />
              <Route path="/teacher/classes/:id/live" element={<Protected><TeacherLiveMonitor /></Protected>} />
              <Route path="/terms/teacher" element={<TeacherTerms />} />
              <Route path="/schools/purchase-order" element={<PurchaseOrderRequest />} />
              {/* Student flow — separate cookie, no <Protected> wrapper (StudentLogin renders self, others self-check) */}
              <Route path="/student/login" element={<StudentLogin />} />
              <Route path="/student/login-guide" element={<StudentLoginGuide />} />
              <Route path="/student/onboarding" element={<StudentOnboarding />} />
              <Route path="/student/dashboard" element={<StudentDashboard />} />
              <Route path="/student/expeditions/:id" element={<StudentExpeditionView />} />
              <Route path="/admin/setup" element={<AdminSetup />} />
              <Route path="/admin" element={<Protected><Admin /></Protected>} />
              <Route path="/admin/dashboard" element={<Protected><Admin /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
